import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  Option,
  showNotification,
} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {
  ListVehicleFinancingTemplateResponseBody,
  useArchiveVehicleFinancingTemplateMutation,
  VehicleFinancingTemplateExpirationAction,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {getVehicleFinancingTemplateStatus} from '../../utils/getVehicleFinancingTemplateStatus';
import {archiveFormSchema, ArchiveFormValues} from './schemas/archiveFormSchema';

export interface VehicleFinancingTemplateArchiveDialogProps {
  template: ListVehicleFinancingTemplateResponseBody;
  templates: ListVehicleFinancingTemplateResponseBody[];
  /** Called after a successful archive, so a host dialog can collapse the archived card. */
  onArchived?: VoidFunction;
}

interface ArchiveDialogFieldsProps {
  control: FormControl<ArchiveFormValues>;
  isLoading: boolean;
  actionOptions: Option<VehicleFinancingTemplateExpirationAction>[];
  replacementOptions: Option[];
}

export function VehicleFinancingTemplateArchiveDialog(
  props: VehicleFinancingTemplateArchiveDialogProps
) {
  const [archiveTemplate, {isLoading}] = useArchiveVehicleFinancingTemplateMutation();
  const replacementOptions: Option[] = props.templates
    .filter(
      (template) =>
        template.id !== props.template.id &&
        template.type === props.template.type &&
        getVehicleFinancingTemplateStatus(template) === 'ACTIVE'
    )
    .map((template) => ({value: template.id, label: template.name}));
  const actionOptions = ARCHIVE_ACTION_OPTIONS.filter(
    (option) => option.value === 'REMOVE' || replacementOptions.length > 0
  );

  const handleArchiveSuccess = () => {
    closeCurrentDialog();
    showNotification.success(
      i18n.t('entity.vehicleFinancingTemplate.notifications.successfullyArchived')
    );
    props.onArchived?.();
  };

  const handleSubmit: FormSubmitHandler<ArchiveFormValues> = (values) =>
    archiveTemplate({
      vehicleFinancingTemplateId: props.template.id,
      archiveVehicleFinancingTemplateRequestBody: {
        action: values.action,
        replacementTemplateId: values.action === 'REPLACE' ? values.replacementTemplateId : null,
      },
    })
      .unwrap()
      .then(handleArchiveSuccess)
      .catch(handleApiError);

  return (
    <Form<ArchiveFormValues>
      experimentalZodSchema={archiveFormSchema}
      defaultValues={{
        action: 'REMOVE',
        replacementTemplateId: null,
      }}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          <Text>
            {i18n.t('entity.vehicleFinancingTemplate.archiveDialog.description', {
              name: props.template.name,
            })}
          </Text>
          <Text size="xSmall" color="secondary">
            {i18n.t('entity.vehicleFinancingTemplate.archiveDialog.advertisementRefresh')}
          </Text>
          <ArchiveDialogFields
            control={control}
            isLoading={isLoading}
            actionOptions={actionOptions}
            replacementOptions={replacementOptions}
          />
        </VStack>
      )}
    </Form>
  );
}

function ArchiveDialogFields(props: ArchiveDialogFieldsProps) {
  const action = useWatch({control: props.control, name: 'action'});

  return (
    <>
      <FormField
        control={props.control}
        name="action"
        type="radio"
        isRequired
        label={i18n.t('entity.vehicleFinancingTemplate.archiveDialog.existingVehicles')}
        tooltip={i18n.t('entity.vehicleFinancingTemplate.archiveDialog.existingVehiclesTooltip')}
        options={props.actionOptions}
        data-testid="vehicleFinancingTemplate-archiveDialog-action"
      />
      <Show when={action === 'REPLACE'}>
        <FormField
          control={props.control}
          name="replacementTemplateId"
          type="choice"
          isRequired
          label={i18n.t('entity.vehicleFinancingTemplate.archiveDialog.replacementTemplate')}
          options={props.replacementOptions}
          menuInPortal
          data-testid="vehicleFinancingTemplate-archiveDialog-replacement"
        />
      </Show>
      <ButtonGroup align="right">
        <Button
          variant="secondary"
          title={i18n.t('general.actions.cancel')}
          onClick={closeCurrentDialog}
          isDisabled={props.isLoading}
        />
        <FormButton
          control={props.control}
          type="submit"
          title={i18n.t('general.actions.archive')}
          leftIcon="content/archive"
          isLoading={props.isLoading}
          isDisabled={props.isLoading}
        />
      </ButtonGroup>
    </>
  );
}

const ARCHIVE_ACTION_OPTIONS: Option<VehicleFinancingTemplateExpirationAction>[] = [
  {
    value: 'REMOVE',
    label: i18n.t('entity.vehicleFinancingTemplate.expirationAction.remove'),
  },
  {
    value: 'REPLACE',
    label: i18n.t('entity.vehicleFinancingTemplate.expirationAction.replace'),
  },
];
